import React, { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { useToast } from '../../hooks/useToast';
import { functions } from '../../lib/firebase';
import { httpsCallable } from 'firebase/functions';

export default function SystemTools() {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [fixingOffer, setFixingOffer] = useState(false);
  const [offerId, setOfferId] = useState('');
  const [fixResult, setFixResult] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSyncRoutes = async () => {
    setSyncing(true);
    setSyncResult(null);

    try {
      const syncPublicRoutes = httpsCallable(functions, 'syncPublicRoutes');
      const result = await syncPublicRoutes();
      const data = result.data as any;

      setSyncResult(data.message);
      toast({
        type: 'success',
        title: 'Routes Synced',
        description: `${data.created} created, ${data.updated} updated. Total: ${data.total} routes.`,
      });
    } catch (error: any) {
      console.error('Error syncing routes:', error);
      toast({
        type: 'error',
        title: 'Sync Failed',
        description: error.message || 'Failed to sync routes',
      });
      setSyncResult(`Error: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleFixOfferStatus = async () => {
    if (!offerId.trim()) {
      toast({
        type: 'error',
        title: 'Offer ID Required',
        description: 'Please enter an offer ID',
      });
      return;
    }

    setFixingOffer(true);
    setFixResult(null);

    try {
      const fixOfferStatus = httpsCallable(functions, 'fixOfferStatus');
      const result = await fixOfferStatus({ offerId: offerId.trim() });
      const data = result.data as any;

      setFixResult(data.message);
      toast({
        type: 'success',
        title: 'Offer Status Fixed',
        description: data.message,
      });

      // Clear the input after success
      setOfferId('');
    } catch (error: any) {
      console.error('Error fixing offer status:', error);
      const errorMessage = error.message || 'Failed to fix offer status';
      toast({
        type: 'error',
        title: 'Fix Failed',
        description: errorMessage,
      });
      setFixResult(`Error: ${errorMessage}`);
    } finally {
      setFixingOffer(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">System Tools</h1>
        <p className="text-gray-600 mb-8">Administrative utilities and system maintenance</p>

        <div className="space-y-6">
          {/* Sync Public Routes */}
          <Card>
            <CardHeader>
              <CardTitle>Sync Public Routes</CardTitle>
              <CardDescription>
                Populate or update the publicRoutes collection with all HUBSTOP routes.
                This will create/update all 12 routes that appear on the /routes page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4">
                <Button
                  onClick={handleSyncRoutes}
                  loading={syncing}
                  variant="shuttlebee"
                >
                  Sync Routes
                </Button>
                {syncResult && (
                  <div className="flex-1">
                    <div className="p-3 bg-gray-100 rounded-md">
                      <p className="text-sm text-gray-700">{syncResult}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p className="font-semibold mb-2">What this does:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Creates routes for all 12 HUBSTOPS</li>
                  <li>Sets all routes to active (isActive: true)</li>
                  <li>Updates existing routes with current data</li>
                  <li>Routes will immediately appear on /routes page</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Fix Offer Status */}
          <Card>
            <CardHeader>
              <CardTitle>Fix Offer Status</CardTitle>
              <CardDescription>
                Manually update an offer's status when a payment completes but the webhook doesn't process correctly.
                This fixes offers stuck as "Offer Sent" when they should be "Offer Accepted".
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label htmlFor="offerId" className="block text-sm font-medium text-gray-700 mb-2">
                    Offer ID
                  </label>
                  <input
                    id="offerId"
                    type="text"
                    value={offerId}
                    onChange={(e) => setOfferId(e.target.value)}
                    placeholder="Enter offer ID (e.g., o3bYSQhCzgBNAQhXTJdY)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-shuttlebee-yellow focus:border-shuttlebee-yellow"
                  />
                </div>
                <div className="flex items-start gap-4">
                  <Button
                    onClick={handleFixOfferStatus}
                    loading={fixingOffer}
                    variant="shuttlebee"
                    disabled={!offerId.trim()}
                  >
                    Fix Offer Status
                  </Button>
                  {fixResult && (
                    <div className="flex-1">
                      <div className="p-3 bg-gray-100 rounded-md">
                        <p className="text-sm text-gray-700">{fixResult}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <p className="font-semibold mb-2">What this does:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Checks if the offer has any paid recipients</li>
                    <li>Updates the offer status from "active" to "partially_filled"</li>
                    <li>The admin portal will then show it as "Offer Accepted"</li>
                    <li>Only works if at least one recipient has paid</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Future tools can be added here */}
          <Card className="opacity-50">
            <CardHeader>
              <CardTitle>Database Backup</CardTitle>
              <CardDescription>
                Export database collections for backup (Coming Soon)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button disabled variant="outline">
                Export Data
              </Button>
            </CardContent>
          </Card>

          <Card className="opacity-50">
            <CardHeader>
              <CardTitle>Clear Test Data</CardTitle>
              <CardDescription>
                Remove test quotes and offers from staging (Coming Soon)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button disabled variant="outline">
                Clear Test Data
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
